export type EntitlementAccess = "free" | "pro" | "lapsed";

export interface EntitlementDocument {
  status?: unknown;
  plan?: unknown;
  source?: unknown;
  productId?: unknown;
  expiresAt?: unknown;
  updatedAt?: unknown;
  [extension: string]: unknown;
}

const foreverTokens = new Set([
  "early_adopter",
  "early_adopter_forever",
  "lifetime",
  "admin_test",
]);
const paidStatuses = new Set(["active", "paid_active", "trial", "trialing", "grace"]);
const lapsedStatuses = new Set([
  "cancelled",
  "canceled",
  "expired",
  "revoked",
  "lapsed",
  "unpaid",
  "incomplete_expired",
]);

function token(value: unknown): string {
  return typeof value === "string" ? value.trim().toLocaleLowerCase("en-GB") : "";
}

function timestampMillis(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 100_000_000_000 ? value : value * 1_000;
  }
  if (value && typeof value === "object") {
    if ("toDate" in value && typeof (value as { toDate?: unknown }).toDate === "function") {
      return (value as { toDate: () => Date }).toDate().getTime();
    }
    if ("seconds" in value && typeof (value as { seconds?: unknown }).seconds === "number") {
      return (value as { seconds: number }).seconds * 1_000;
    }
  }
  return null;
}

/** Mirrors public/docs/ENTITLEMENT_SPEC.md in the production backend. */
export function evaluateEntitlement(
  document: EntitlementDocument | null | undefined,
  now = new Date(),
): EntitlementAccess {
  if (!document) return "free";
  const status = token(document.status);
  const plan = token(document.plan);
  const expiry = timestampMillis(document.expiresAt);

  if (foreverTokens.has(status) || foreverTokens.has(plan)) return "pro";
  if (status === "institution" || plan === "institution") {
    return expiry !== null && expiry <= now.getTime() ? "lapsed" : "pro";
  }
  if (lapsedStatuses.has(status)) return "lapsed";
  if (paidStatuses.has(status)) {
    return expiry !== null && expiry > now.getTime() ? "pro" : "lapsed";
  }
  return "free";
}

